import type { QuotaMeStatusDTO } from '@shared/schemas/quota';

export type QuotaConstraintSource = 'company' | 'user';

export type EffectiveQuotaConstraint = {
  blocked: boolean;
  ratio: number | null;
  resetAt: string | null;
  source: QuotaConstraintSource;
  tokensRemaining: number | null;
};

function getCompanyRatio(status: QuotaMeStatusDTO) {
  const allowance = status.company.monthlyTokenLimit === null
    ? null
    : status.company.monthlyTokenLimit + status.company.extraTokens;

  if (allowance === null) return null;
  if (allowance === 0) return status.company.tokensConsumed > 0 ? 1 : 0;
  return status.company.tokensConsumed / allowance;
}

function getCompanyRemaining(status: QuotaMeStatusDTO) {
  if (status.company.monthlyTokenLimit === null) return null;
  return Math.max(
    0,
    status.company.monthlyTokenLimit
      + status.company.extraTokens
      - status.company.tokensConsumed,
  );
}

export function getEffectiveQuotaConstraint(
  status: QuotaMeStatusDTO,
): EffectiveQuotaConstraint {
  const companyRatio = getCompanyRatio(status);
  const userRatio = status.enforced ? status.usageRatio : null;
  const companyIsStricter = status.company.blocked && status.blocked
    ? (companyRatio ?? 0) >= (userRatio ?? 0)
    : status.company.blocked
      || (
        !status.blocked
        && companyRatio !== null
        && (userRatio === null || companyRatio >= userRatio)
      );

  if (companyIsStricter) {
    return {
      blocked: status.company.blocked,
      ratio: companyRatio,
      resetAt: status.company.resetAt,
      source: 'company',
      tokensRemaining: getCompanyRemaining(status),
    };
  }

  return {
    blocked: status.blocked,
    ratio: userRatio,
    resetAt: status.renewsAt,
    source: 'user',
    tokensRemaining: status.tokensRemaining,
  };
}
